import {useEffect, useState} from 'react';

import {useSelector} from 'react-redux';

import {GeolocationPlace, isLocationAccessPermitted} from '~/shared/services/geocoder';
import {selectCurrentAddressKey} from '~/shared/store/storeModules/currentAddressKey/currentAddressKeySelectors';
import useGoogleMapScript from '~/shared/hooks/useGoogleMapScript';
import {getLocalizationService} from '~/shared/services/localisationService';
import useGoogleMapsAutocomplete from '~/shared/components/CustomAddressAutoComplete/useGoogleMapsAutocomplete';
import {trackEvent} from '~/shared/services/analytics';

import {CURRENT_LOCATION_PLACE_ID} from './consts';

const useAddressAutocomplete = ({isMaxMobile, resetAddress, onCurrentLocationClick, onPlaceSelected}: {
  isMaxMobile?: boolean;
  resetAddress?: () => void;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  onCurrentLocationClick?: ({setAddress}: {setAddress: (address: string) => void}) => Promise<void>;
}) => {
  const {t} = getLocalizationService();
  const [address, setAddress] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [locationAccessEnabled, setLocationAccessEnabled] = useState(true);
  const currentAddressKey = useSelector(selectCurrentAddressKey);
  const isGoogleMapReady = useGoogleMapScript();

  const {predictions, geocodeByAddress} = useGoogleMapsAutocomplete(address, 300);

  const currentLocationSuggestion = {
    distanceMeters: null,
    mainText: null,
    placeId: CURRENT_LOCATION_PLACE_ID,
    secondaryText: null,
    text: {
      matches: [],
      text: locationAccessEnabled ? t('use_my_current_location') : t('location_access_denied'),
    },
    types: [],
  } as unknown as google.maps.places.AutocompleteSuggestion['placePrediction'];

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = e.target.value;
    if (newAddress.length === 2) {
      trackEvent('addressStartSearch', {
        searchOption: 'typing',
      });
    }
    resetAddress?.();
    setAddress(newAddress);
  };

  const onInputClick = () => {
    if (isMaxMobile) {
      setAddress('');
      resetAddress?.();
    }

    if (!isInputFocused) {
      setIsInputFocused(true);
    }
  };

  const handleSelect = async (selectedAddressString: string, placeId: string) => {
    resetAddress?.();

    if (placeId === CURRENT_LOCATION_PLACE_ID) {
      await onCurrentLocationClick?.({setAddress});
      setIsInputFocused(false);
      setIsDropdownOpen(false);
      return;
    }

    setAddress(selectedAddressString);
    const results = await geocodeByAddress(selectedAddressString);
    if (!results) {
      return;
    }
    const result: GeolocationPlace = {...results[0], name: results[0].formatted_address};
    onPlaceSelected(result);
    setIsInputFocused(false);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const setLocationAccess = async () => {
      const isLocationAccessible = await isLocationAccessPermitted();
      setLocationAccessEnabled(isLocationAccessible);
    };

    setLocationAccess();
  }, []);

  return {
    address,
    isInputFocused,
    locationAccessEnabled,
    currentAddressKey,
    isLoaded: isGoogleMapReady,
    predictions: [currentLocationSuggestion, ...predictions],
    onChange,
    handleSelect,
    onInputClick,
    onInputFocus: () => {
      setIsInputFocused(true);
      setIsDropdownOpen(true);
    },
    onInputBlur: () => setIsInputFocused(true),
    onInputKeyDown: () => !isInputFocused && setIsInputFocused(true),
    isDropdownOpen,
    closeDropdown: () => setIsDropdownOpen(false),
  };
};

export default useAddressAutocomplete;
